import type { EnterpriseKpis, InsightsKpi, SchoolKpis } from '../types/insights';

/**
 * Versioned in-repo Insights seed (PRO-0590, ADR-038 — `StaticInsightsSource`).
 *
 * ILLUSTRATIVE, NON-LIVE aggregates only (every KPI `isLive:false`, `source:'static'`),
 * hand-authored so the Insights module is demonstrable before the Fabric cutover.
 * NOT PII and NOT governed live figures — Revenue/EBITDA are plausible round numbers,
 * not Aurora's actual finances. Bump `INSIGHTS_SEED_VERSION` on any change (the
 * provenance contract: a static payload is pinned to a known seed version).
 *
 * When `FabricInsightsSource` lands, this file is retired unchanged in shape — the
 * DTOs (`apps/web/src/lib/types/insights.ts`) are the stable contract.
 */
export const INSIGHTS_SEED_VERSION = '2026.07.11-a1';

/** Illustrative "as of" date stamped on every static payload. */
const AS_OF = '2026-06-30';

const ENTERPRISE_KPIS: readonly InsightsKpi[] = [
  {
    id: 'revenue',
    label: 'Revenue',
    kind: 'currency_gbp',
    unit: 'GBP',
    value: 4_820_000,
    chart: 'bar',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 3_910_000 },
      { period: '2026-02', value: 4_050_000 },
      { period: '2026-03', value: 4_240_000 },
      { period: '2026-04', value: 4_480_000 },
      { period: '2026-05', value: 4_690_000 },
      { period: '2026-06', value: 4_820_000 },
    ],
  },
  {
    id: 'ebitda',
    label: 'EBITDA',
    kind: 'currency_gbp',
    unit: 'GBP',
    value: 962_000,
    chart: 'bar',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 742_000 },
      { period: '2026-02', value: 781_000 },
      { period: '2026-03', value: 830_000 },
      { period: '2026-04', value: 884_000 },
      { period: '2026-05', value: 925_000 },
      { period: '2026-06', value: 962_000 },
    ],
  },
  {
    id: 'school_occupancy',
    label: 'School Occupancy',
    kind: 'percentage',
    unit: '%',
    value: 91.4,
    chart: 'line',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 88.1 },
      { period: '2026-02', value: 88.9 },
      { period: '2026-03', value: 89.6 },
      { period: '2026-04', value: 90.2 },
      { period: '2026-05', value: 90.9 },
      { period: '2026-06', value: 91.4 },
    ],
  },
  {
    id: 'parent_nps',
    label: 'Parent NPS',
    kind: 'nps',
    unit: 'NPS',
    value: 48,
    chart: 'line',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 39 },
      { period: '2026-02', value: 41 },
      { period: '2026-03', value: 43 },
      { period: '2026-04', value: 44 },
      { period: '2026-05', value: 46 },
      { period: '2026-06', value: 48 },
    ],
  },
];

const SCHOOL_KPIS: readonly InsightsKpi[] = [
  {
    id: 'student_attendance',
    label: 'Student Attendance',
    kind: 'percentage',
    unit: '%',
    value: 93.7,
    chart: 'line',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 92.1 },
      { period: '2026-02', value: 92.6 },
      { period: '2026-03', value: 93.0 },
      { period: '2026-04', value: 93.2 },
      { period: '2026-05', value: 93.5 },
      { period: '2026-06', value: 93.7 },
    ],
  },
  {
    id: 'behaviour_incidents',
    label: 'Behaviour Incidents',
    kind: 'count',
    unit: 'count',
    value: 27,
    chart: 'bar',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 41 },
      { period: '2026-02', value: 38 },
      { period: '2026-03', value: 35 },
      { period: '2026-04', value: 33 },
      { period: '2026-05', value: 29 },
      { period: '2026-06', value: 27 },
    ],
  },
  {
    id: 'staff_attendance',
    label: 'Staff Attendance',
    kind: 'percentage',
    unit: '%',
    value: 96.2,
    chart: 'line',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 95.1 },
      { period: '2026-02', value: 95.4 },
      { period: '2026-03', value: 95.7 },
      { period: '2026-04', value: 95.9 },
      { period: '2026-05', value: 96.0 },
      { period: '2026-06', value: 96.2 },
    ],
  },
  {
    id: 'school_occupancy',
    label: 'School Occupancy',
    kind: 'percentage',
    unit: '%',
    value: 89.8,
    chart: 'line',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 86.9 },
      { period: '2026-02', value: 87.5 },
      { period: '2026-03', value: 88.2 },
      { period: '2026-04', value: 88.8 },
      { period: '2026-05', value: 89.3 },
      { period: '2026-06', value: 89.8 },
    ],
  },
];

/**
 * GROUP-LEVEL baseline KPIs for the role-free universal landing (PRO-0610, ADR-038
 * Amendment 1, A1-1). DISTINCT from `SCHOOL_KPIS` — these are group-wide aggregates
 * with group framing (never "your school"), carrying ONLY the two non-confidential
 * baseline metrics (Student Attendance + Behaviour Incidents). Revenue / EBITDA /
 * Parent NPS are deliberately EXCLUDED (Enterprise-only). Staff Attendance and
 * School Occupancy are excluded too (baseline subset). Illustrative, non-live.
 *
 * The KPI `id`s are kept identical to the school-lens ids (`student_attendance`,
 * `behaviour_incidents`) so the Phase-B server-side subset filter keys on the same
 * ids; only the `label`/`value` are group-framed.
 */
const GROUP_BASELINE_KPIS: readonly InsightsKpi[] = [
  {
    id: 'student_attendance',
    label: 'Group Student Attendance',
    kind: 'percentage',
    unit: '%',
    value: 93.1,
    chart: 'line',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 91.4 },
      { period: '2026-02', value: 91.9 },
      { period: '2026-03', value: 92.3 },
      { period: '2026-04', value: 92.6 },
      { period: '2026-05', value: 92.9 },
      { period: '2026-06', value: 93.1 },
    ],
  },
  {
    id: 'behaviour_incidents',
    label: 'Group Behaviour Incidents',
    kind: 'count',
    unit: 'count',
    value: 214,
    chart: 'bar',
    isLive: false,
    source: 'static',
    trend: [
      { period: '2026-01', value: 318 },
      { period: '2026-02', value: 296 },
      { period: '2026-03', value: 271 },
      { period: '2026-04', value: 252 },
      { period: '2026-05', value: 233 },
      { period: '2026-06', value: 214 },
    ],
  },
];

/**
 * Group-level baseline landing payload (PRO-0610 A1-1). Returned as a `SchoolKpis`
 * with `schoolId:null` (group scope, not a per-school roll-up) carrying the two
 * baseline KPIs only. Frozen deep copy so callers cannot mutate the shared seed.
 */
export function landingSeed(asOf: string = AS_OF): SchoolKpis {
  return {
    lens: 'school',
    asOf,
    partial: false,
    schoolId: null,
    kpis: GROUP_BASELINE_KPIS.map((k) => ({ ...k, trend: k.trend.map((t) => ({ ...t })) })),
  };
}

/** Frozen deep copy so callers can never mutate the shared seed. */
export function enterpriseSeed(asOf: string = AS_OF): EnterpriseKpis {
  return {
    lens: 'enterprise',
    asOf,
    kpis: ENTERPRISE_KPIS.map((k) => ({ ...k, trend: k.trend.map((t) => ({ ...t })) })),
  };
}

export function schoolSeed(schoolId: string | null, asOf: string = AS_OF): SchoolKpis {
  return {
    lens: 'school',
    asOf,
    partial: false,
    schoolId,
    kpis: SCHOOL_KPIS.map((k) => ({ ...k, trend: k.trend.map((t) => ({ ...t })) })),
  };
}
